"use client";

import { BreadcrumbsWithNormalizedParent } from "@/hoc/types";
import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";
import { usePathname } from "next/navigation";
interface BreadcrumbsContextType {
  breadcrumbs: BreadcrumbsWithNormalizedParent | null;
  setBreadcrumbs: (breadcrumbs: BreadcrumbsWithNormalizedParent) => void;
}
export const BreadcrumbsContext = createContext<BreadcrumbsContextType | undefined>(undefined);
export const useBreadcrumbs = (): BreadcrumbsContextType => {
  const context = useContext(BreadcrumbsContext);
  if (!context) {
    throw new Error("useBreadcrumbs must be used within a BreadcrumbsProvider");
  }
  return context;
};
interface BreadcrumbsProviderProps {
  children: ReactNode;
}
const parseBreadcrumbsFromMeta = (): BreadcrumbsWithNormalizedParent | null => {
  if (typeof document !== "undefined") {
    const metaTag = document.querySelector('meta[name="breadcrumbs"]');
    if (metaTag) {
      const content = metaTag.getAttribute("content");
      if (content) {
        return JSON.parse(content);
      }
    }
  }
  return null;
};
export const BreadcrumbsProvider: React.FC<BreadcrumbsProviderProps> = ({
  children
}) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbsWithNormalizedParent | null>(parseBreadcrumbsFromMeta);
  const memoizedSetBreadcrumbs = useCallback((newBreadcrumbs: BreadcrumbsWithNormalizedParent) => {
    setBreadcrumbs(newBreadcrumbs);
  }, []);
  const pathname = usePathname();
  useEffect(() => {
    const initialBreadcrumbs = parseBreadcrumbsFromMeta();
    if (initialBreadcrumbs) {
      setBreadcrumbs(initialBreadcrumbs);
    }
    const handleRouteChange = () => {
      const updatedBreadcrumbs = parseBreadcrumbsFromMeta();
      setBreadcrumbs(updatedBreadcrumbs);
    };
    handleRouteChange();
    const metaTag = document.querySelector('meta[name="breadcrumbs"]');
    if (metaTag) {
      const observer = new MutationObserver(() => {
        const updatedBreadcrumbs = parseBreadcrumbsFromMeta();
        if (updatedBreadcrumbs) {
          setBreadcrumbs(updatedBreadcrumbs);
        }
      });
      observer.observe(metaTag, {
        attributes: true,
        attributeFilter: ["content"]
      });
      return () => {
        observer.disconnect();
      };
    }
  }, [pathname]);
  return <BreadcrumbsContext.Provider value={{
    breadcrumbs,
    setBreadcrumbs
  }} data-sentry-element="unknown" data-sentry-component="BreadcrumbsProvider" data-sentry-source-file="BreadcrumbsContext.tsx">
      {children}
    </BreadcrumbsContext.Provider>;
};